// Static spotlight data for the most-requested destinations. Kept in-memory to avoid
// an external API call on every open — weather + FX are conservative estimates refreshed
// each season. Used by <DestinationSpotlight/>.
//
// Shape:
//  { name, hero, weather, bestTime, currency, inrRate, visa, priceFrom, tagline,
//    attractions: [{name, img, blurb}] }
//
// For any destination NOT present here, getSpotlight() returns a smart fallback that
// title-cases the user's input and uses a reliable generic travel hero.

export const SPOTLIGHTS = {
  goa: {
    name: "Goa",
    hero: "https://images.unsplash.com/photo-1512343879784-a960bf40e7f2?w=2000&q=80",
    tagline: "Sun, Sand & Susegaad",
    weather: "28–32°C · warm & breezy",
    bestTime: "Nov – Feb (dry season)",
    currency: "INR (domestic)",
    inrRate: "No FX",
    visa: "Domestic — No visa",
    priceFrom: "From ₹18,000 pp · 3N/4D",
    attractions: [
      { name: "Baga & Anjuna Beaches", img: "https://images.unsplash.com/photo-1582972236019-ea4af5ffe587?w=800&q=80", blurb: "Iconic North Goa shacks, parties and Saturday markets." },
      { name: "Old Goa Churches", img: "https://images.unsplash.com/photo-1567472429706-c2f30040ca50?w=800&q=80", blurb: "UNESCO-listed Portuguese-era basilicas, best at golden hour." },
      { name: "Palolem & Agonda", img: "https://images.unsplash.com/photo-1590080874088-eec64895b423?w=800&q=80", blurb: "Quieter South Goa crescents for long slow sunsets." },
    ],
  },
  kashmir: {
    name: "Kashmir",
    hero: "https://images.unsplash.com/photo-1605649487212-47bdab064df7?w=2000&q=80",
    tagline: "Paradise on Earth",
    weather: "5–25°C · cool summers, snow Dec–Feb",
    bestTime: "Apr – Oct (or Dec for snow)",
    currency: "INR (domestic)",
    inrRate: "No FX",
    visa: "Domestic — No visa",
    priceFrom: "From ₹24,000 pp · 4N/5D",
    attractions: [
      { name: "Dal Lake Shikara", img: "https://images.unsplash.com/photo-1593181629936-11c609b8db9b?w=800&q=80", blurb: "Sunrise houseboat breakfast on Srinagar's legendary lake." },
      { name: "Gulmarg Gondola", img: "https://images.unsplash.com/photo-1566559532215-bbc9b4cc6d4a?w=800&q=80", blurb: "Asia's highest cable car — Himalayan views, ski-season gold." },
      { name: "Pahalgam Valley", img: "https://images.unsplash.com/photo-1519451241324-20b4ea2c4220?w=800&q=80", blurb: "Meadows, pine forests and the Lidder river — slow travel at its best." },
    ],
  },
  bali: {
    name: "Bali",
    hero: "https://images.unsplash.com/photo-1537996194471-e657df975ab4?w=2000&q=80",
    tagline: "Island of the Gods",
    weather: "26–32°C · tropical year-round",
    bestTime: "Apr – Oct (dry season)",
    currency: "IDR (Indonesian Rupiah)",
    inrRate: "1 INR ≈ 188 IDR",
    visa: "Visa on Arrival (~USD 35)",
    priceFrom: "From ₹65,000 pp · 5N/6D",
    attractions: [
      { name: "Ubud Rice Terraces", img: "https://images.unsplash.com/photo-1573790387438-4da905039392?w=800&q=80", blurb: "Tegallalang's emerald-green valley, best at sunrise with a coconut." },
      { name: "Uluwatu Temple", img: "https://images.unsplash.com/photo-1604999565976-8913ad2ddb7c?w=800&q=80", blurb: "Cliff-edge temple + the Kecak fire dance at sundown." },
      { name: "Nusa Penida", img: "https://images.unsplash.com/photo-1582805549317-2ba83cdbf3b4?w=800&q=80", blurb: "Day trip to Kelingking Beach — the T-Rex cliff of Instagram fame." },
    ],
  },
  singapore: {
    name: "Singapore",
    hero: "https://images.unsplash.com/photo-1525625293386-3f8f99389edd?w=2000&q=80",
    tagline: "Asia's Future, Now",
    weather: "25–31°C · humid year-round",
    bestTime: "Feb – Apr (driest window)",
    currency: "SGD (Singapore Dollar)",
    inrRate: "1 SGD ≈ ₹64",
    visa: "e-Visa required for Indians",
    priceFrom: "From ₹58,000 pp · 4N/5D",
    attractions: [
      { name: "Marina Bay & Gardens", img: "https://images.unsplash.com/photo-1508964942454-1a56651d54ac?w=800&q=80", blurb: "Supertree Grove light show + the Sands SkyPark at blue hour." },
      { name: "Sentosa & Universal", img: "https://images.unsplash.com/photo-1567360425618-1594206637d2?w=800&q=80", blurb: "Family theme park day — Transformers ride is the queue-beater." },
      { name: "Hawker Food Trail", img: "https://images.unsplash.com/photo-1555921015-5532091f6026?w=800&q=80", blurb: "Maxwell, Tiong Bahru, Old Airport Rd — Michelin stalls for ₹300." },
    ],
  },
  maldives: {
    name: "Maldives",
    hero: "https://images.pexels.com/photos/1287460/pexels-photo-1287460.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=1800",
    tagline: "Overwater Romance",
    weather: "26–31°C · sunny, occasional showers",
    bestTime: "Nov – Apr (dry season)",
    currency: "MVR (Maldivian Rufiyaa)",
    inrRate: "1 MVR ≈ ₹5.5",
    visa: "Free Visa on Arrival (30 days)",
    priceFrom: "From ₹1,25,000 pp · 3N/4D",
    attractions: [
      { name: "Overwater Villas", img: "https://images.unsplash.com/photo-1540541338287-41700207dee6?w=800&q=80", blurb: "Your own slice of the Indian Ocean — ladders straight into the reef." },
      { name: "Manta Ray Snorkelling", img: "https://images.unsplash.com/photo-1540202404-1b927e27fa8b?w=800&q=80", blurb: "Baa Atoll's Hanifaru Bay from June to Nov — a natural aquarium." },
      { name: "Sandbank Picnic", img: "https://images.unsplash.com/photo-1578922746465-3a80a228f223?w=800&q=80", blurb: "Private boat drop on an uninhabited sandbar with champagne." },
    ],
  },
  dubai: {
    name: "Dubai",
    hero: "https://images.unsplash.com/photo-1512453979798-5ea266f8880c?w=2000&q=80",
    tagline: "Glamour Meets Desert",
    weather: "20–40°C · best when not peak summer",
    bestTime: "Nov – Mar (cooler months)",
    currency: "AED (UAE Dirham)",
    inrRate: "1 AED ≈ ₹23",
    visa: "e-Visa required for Indians",
    priceFrom: "From ₹48,000 pp · 4N/5D",
    attractions: [
      { name: "Burj Khalifa At The Top", img: "https://images.unsplash.com/photo-1518684079-3c830dcef090?w=800&q=80", blurb: "124+148 floor combo — skip the single-floor ticket, you'll regret it." },
      { name: "Desert Safari", img: "https://images.unsplash.com/photo-1546412414-e1885e51cfa5?w=800&q=80", blurb: "Dune drive, falconry, Bedouin dinner — the private camps are worth it." },
      { name: "Old Dubai Creek Abra", img: "https://images.unsplash.com/photo-1512632578888-169bbbc64f33?w=800&q=80", blurb: "5-minute wooden-boat ride for AED 1 — the best-value ride in the city." },
    ],
  },
  thailand: {
    name: "Thailand",
    hero: "https://images.unsplash.com/photo-1508009603885-50cf7c579365?w=2000&q=80",
    tagline: "Land of Smiles",
    weather: "24–35°C · hot, monsoon Jun–Oct",
    bestTime: "Nov – Feb (cool & dry)",
    currency: "THB (Thai Baht)",
    inrRate: "1 THB ≈ ₹2.5",
    visa: "Free Visa on Arrival (30 days)",
    priceFrom: "From ₹42,000 pp · 5N/6D",
    attractions: [
      { name: "Railay Beach, Krabi", img: "https://images.unsplash.com/photo-1528181304800-259b08848526?w=800&q=80", blurb: "Limestone cliffs + turquoise water accessible only by longtail boat." },
      { name: "Chiang Mai Temples", img: "https://images.unsplash.com/photo-1552465011-b4e21bf6e79a?w=800&q=80", blurb: "Doi Suthep at dawn, Sunday walking street food, ethical elephant camps." },
      { name: "Phi Phi Island Hopper", img: "https://images.unsplash.com/photo-1563492065599-3520f775eeed?w=800&q=80", blurb: "4 Islands day boat — Tup, Chicken, Poda, Phra Nang." },
    ],
  },
  "sri lanka": {
    name: "Sri Lanka",
    hero: "https://images.unsplash.com/photo-1566296314736-6eaac1ca0cb9?w=2000&q=80",
    tagline: "Pearl of the Indian Ocean",
    weather: "25–32°C · tropical, two monsoons",
    bestTime: "Dec – Mar (west & south coasts)",
    currency: "LKR (Sri Lankan Rupee)",
    inrRate: "1 LKR ≈ ₹0.29",
    visa: "ETA online · ~USD 50 · 30 days",
    priceFrom: "From ₹35,000 pp · 5N/6D",
    attractions: [
      { name: "Sigiriya Lion Rock", img: "https://images.unsplash.com/photo-1586804467723-c38a85ef6bc9?w=800&q=80", blurb: "UNESCO fortress sunrise climb — 1,200 ancient steps to a royal palace in the sky." },
      { name: "Ella Kandy Train", img: "https://images.unsplash.com/photo-1596423349923-bd9b15c1ed43?w=800&q=80", blurb: "World's most scenic train ride — tea plantations, viaducts, open-door photos." },
      { name: "Mirissa Whale Safari", img: "https://images.unsplash.com/photo-1583836631369-4a3c5e7b6a30?w=800&q=80", blurb: "Nov–Apr sightings of blue whales + beach-town cocktails at Coconut Tree Hill." },
    ],
  },
  vietnam: {
    name: "Vietnam",
    hero: "https://images.unsplash.com/photo-1528127269322-539801943592?w=2000&q=80",
    tagline: "Timeless Charm",
    weather: "22–33°C · tropical, regional variance",
    bestTime: "Nov – Apr (most areas dry)",
    currency: "VND (Vietnamese Dong)",
    inrRate: "1 INR ≈ 295 VND",
    visa: "e-Visa · ~USD 25 · 30 days",
    priceFrom: "From ₹52,000 pp · 6N/7D",
    attractions: [
      { name: "Ha Long Bay Cruise", img: "https://images.unsplash.com/photo-1528127269322-539801943592?w=800&q=80", blurb: "Overnight junk boat through 1,600 emerald limestone karsts — UNESCO wonder." },
      { name: "Hoi An Lanterns", img: "https://images.unsplash.com/photo-1540541338287-41700207dee6?w=800&q=80", blurb: "500-year-old trading port lit by 1,000 silk lanterns after sunset." },
      { name: "Sapa Rice Terraces", img: "https://images.unsplash.com/photo-1528181304800-259b08848526?w=800&q=80", blurb: "Trek Hmong villages through cloud-wrapped hillside rice fields in the north." },
    ],
  },
  japan: {
    name: "Japan",
    hero: "https://images.unsplash.com/photo-1540959733332-eab4deabeeaf?w=2000&q=80",
    tagline: "Tradition Meets Tomorrow",
    weather: "5–30°C · four distinct seasons",
    bestTime: "Mar – May (sakura) · Oct – Nov (koyo)",
    currency: "JPY (Japanese Yen)",
    inrRate: "1 JPY ≈ ₹0.55",
    visa: "e-Visa required · 15-day process",
    priceFrom: "From ₹1,45,000 pp · 7N/8D",
    attractions: [
      { name: "Kyoto Cherry Blossoms", img: "https://images.unsplash.com/photo-1528164344705-47542687000d?w=800&q=80", blurb: "Philosopher's Path + Arashiyama bamboo — peak bloom early April." },
      { name: "Tokyo Shibuya & Ginza", img: "https://images.unsplash.com/photo-1540959733332-eab4deabeeaf?w=800&q=80", blurb: "World's busiest crossing, Michelin-starred ramen, Harajuku street style." },
      { name: "Mt. Fuji & Hakone", img: "https://images.unsplash.com/photo-1570459027562-4a916cc4d50a?w=800&q=80", blurb: "Onsen hot-spring ryokan stay with a perfect cone view from your bath." },
    ],
  },
  switzerland: {
    name: "Switzerland",
    hero: "https://images.unsplash.com/photo-1531366936337-7c912a4589a7?w=2000&q=80",
    tagline: "The Alps, Perfected",
    weather: "-5 – 25°C · snow Dec–Mar, lush Jun–Sep",
    bestTime: "Jun – Sep (summer) · Dec – Mar (ski)",
    currency: "CHF (Swiss Franc)",
    inrRate: "1 CHF ≈ ₹105",
    visa: "Schengen visa required · 15-day process",
    priceFrom: "From ₹1,85,000 pp · 6N/7D",
    attractions: [
      { name: "Jungfraujoch — Top of Europe", img: "https://images.unsplash.com/photo-1527668752968-14dc70a27c95?w=800&q=80", blurb: "Cogwheel train to 3,454 m — glacier palace, Sphinx Observatory, snow in July." },
      { name: "Glacier Express", img: "https://images.unsplash.com/photo-1531366936337-7c912a4589a7?w=800&q=80", blurb: "Zermatt to St. Moritz — world's slowest express, 8 hours of panorama windows." },
      { name: "Lake Lucerne Mt. Pilatus", img: "https://images.unsplash.com/photo-1582652119502-2feac5b1f6d2?w=800&q=80", blurb: "Golden Round Trip — boat, cogwheel, cable car, in one unforgettable day." },
    ],
  },
  greece: {
    name: "Greece",
    hero: "https://images.unsplash.com/photo-1533105079780-92b9be482077?w=2000&q=80",
    tagline: "Cyclades Blue",
    weather: "18–32°C · dry summers, mild winters",
    bestTime: "May – Jun, Sep – Oct (shoulder)",
    currency: "EUR (Euro)",
    inrRate: "1 EUR ≈ ₹92",
    visa: "Schengen visa required · 15-day process",
    priceFrom: "From ₹1,65,000 pp · 6N/7D",
    attractions: [
      { name: "Santorini Caldera", img: "https://images.unsplash.com/photo-1533105079780-92b9be482077?w=800&q=80", blurb: "Oia sunset from a cave-house infinity pool — earth's most photographed view." },
      { name: "Mykonos Windmills", img: "https://images.unsplash.com/photo-1601581875039-e899893d520c?w=800&q=80", blurb: "Little Venice bars, Paraga beach clubs, superyacht watching at Scorpios." },
      { name: "Athens Acropolis", img: "https://images.unsplash.com/photo-1555993539-1732b0258235?w=800&q=80", blurb: "The Parthenon at golden hour + Plaka tavernas for moussaka and ouzo." },
    ],
  },
  andaman: {
    name: "Andaman",
    hero: "https://images.unsplash.com/photo-1586500036706-41963de24d8b?w=2000&q=80",
    tagline: "India's Own Maldives",
    weather: "24–31°C · tropical, monsoon May–Sep",
    bestTime: "Oct – Apr (calm seas, clear skies)",
    currency: "INR (domestic)",
    inrRate: "No FX",
    visa: "Domestic — No visa",
    priceFrom: "From ₹45,000 pp · 5N/6D",
    attractions: [
      { name: "Radhanagar Beach, Havelock", img: "https://images.unsplash.com/photo-1586500036706-41963de24d8b?w=800&q=80", blurb: "Asia's #1 beach — powder-white sand and Gatorade-blue water." },
      { name: "Scuba at Elephant Beach", img: "https://images.unsplash.com/photo-1582967788606-a171c1080cb0?w=800&q=80", blurb: "Beginner-friendly try-dives with turtles, clownfish and soft corals." },
      { name: "Cellular Jail Light Show", img: "https://images.unsplash.com/photo-1609151376730-f246ec0b99e5?w=800&q=80", blurb: "Port Blair's freedom-struggle memorial — emotional evening tribute." },
    ],
  },
  ladakh: {
    name: "Ladakh",
    hero: "https://images.unsplash.com/photo-1519451241324-20b4ea2c4220?w=2000&q=80",
    tagline: "Moon on Earth",
    weather: "-10 – 22°C · cold desert, Jun–Sep open",
    bestTime: "Jun – Sep (passes open)",
    currency: "INR (domestic)",
    inrRate: "No FX",
    visa: "Domestic — No visa · Inner-Line Permit",
    priceFrom: "From ₹38,000 pp · 6N/7D",
    attractions: [
      { name: "Pangong Tso Lake", img: "https://images.unsplash.com/photo-1519451241324-20b4ea2c4220?w=800&q=80", blurb: "4,350 m high, shifts from turquoise to violet through the day — 3 Idiots finale spot." },
      { name: "Nubra Valley & Hunder", img: "https://images.unsplash.com/photo-1574236170880-fbc8a5bb0b97?w=800&q=80", blurb: "Bactrian double-hump camel safari on high-altitude cold-desert dunes." },
      { name: "Khardung La Pass", img: "https://images.unsplash.com/photo-1470770841072-f978cf4d019e?w=800&q=80", blurb: "One of the world's highest motorable roads — 17,582 ft selfie trophy." },
    ],
  },
  nepal: {
    name: "Nepal",
    hero: "https://images.unsplash.com/photo-1544735716-392fe2489ffa?w=2000&q=80",
    tagline: "Home of the Himalayas",
    weather: "0–28°C · cold mountains, warm Terai",
    bestTime: "Oct – Nov, Mar – May (clear skies)",
    currency: "NPR (Nepalese Rupee)",
    inrRate: "1 INR ≈ 1.6 NPR",
    visa: "Free · On-Arrival for Indians",
    priceFrom: "From ₹22,000 pp · 4N/5D",
    attractions: [
      { name: "Kathmandu Durbar Squares", img: "https://images.unsplash.com/photo-1544735716-392fe2489ffa?w=800&q=80", blurb: "Pashupatinath, Swayambhunath, Boudha — three UNESCO sites in one city." },
      { name: "Pokhara Phewa Lake", img: "https://images.unsplash.com/photo-1580537659466-0a9bfa916a54?w=800&q=80", blurb: "Paragliding, sunrise at Sarangkot, and the Annapurna reflection at dawn." },
      { name: "Everest Mountain Flight", img: "https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?w=800&q=80", blurb: "60-minute scenic flight — close-up cockpit view of the world's highest peak." },
    ],
  },
  bhutan: {
    name: "Bhutan",
    hero: "https://images.unsplash.com/photo-1566296314736-6eaac1ca0cb9?w=2000&q=80",
    tagline: "Last Shangri-La",
    weather: "5–28°C · cool mountain climate",
    bestTime: "Mar – May, Sep – Nov",
    currency: "BTN (pegged to INR)",
    inrRate: "1 BTN = ₹1",
    visa: "Permit-based · No visa for Indians",
    priceFrom: "From ₹55,000 pp · 5N/6D (incl. SDF)",
    attractions: [
      { name: "Tiger's Nest Monastery", img: "https://images.unsplash.com/photo-1566296314736-6eaac1ca0cb9?w=800&q=80", blurb: "Paro's cliff-hanging Taktsang — 3-hour hike rewarded by the country's icon." },
      { name: "Thimphu Tashichho Dzong", img: "https://images.unsplash.com/photo-1593181629936-11c609b8db9b?w=800&q=80", blurb: "Fortress-monastery that houses both the King and the chief abbot." },
      { name: "Punakha Valley Suspension Bridge", img: "https://images.unsplash.com/photo-1605649487212-47bdab064df7?w=800&q=80", blurb: "180-m prayer-flag span over the Po Chhu river — Bhutan's longest." },
    ],
  },
  turkey: {
    name: "Turkey",
    hero: "https://images.unsplash.com/photo-1527838832700-5059252407fa?w=2000&q=80",
    tagline: "Where East Meets West",
    weather: "8–32°C · Mediterranean, snowy interior",
    bestTime: "Apr – Jun, Sep – Oct (mild)",
    currency: "TRY (Turkish Lira)",
    inrRate: "1 TRY ≈ ₹2.6",
    visa: "e-Visa · ~USD 60 · instant",
    priceFrom: "From ₹68,000 pp · 6N/7D",
    attractions: [
      { name: "Cappadocia Hot-Air Balloons", img: "https://images.unsplash.com/photo-1527838832700-5059252407fa?w=800&q=80", blurb: "Dawn flight over fairy chimneys — bucket-list sunrise for every photographer." },
      { name: "Istanbul Hagia Sophia", img: "https://images.unsplash.com/photo-1541432901042-2d8bd64b4a9b?w=800&q=80", blurb: "1,500 years of Byzantine + Ottoman history + Grand Bazaar spice trails." },
      { name: "Pamukkale Thermal Terraces", img: "https://images.unsplash.com/photo-1526998763823-b79e93f6d9a0?w=800&q=80", blurb: "White travertine pools flowing over a hillside — sunset bathing is unreal." },
    ],
  },
  malaysia: {
    name: "Malaysia",
    hero: "https://images.unsplash.com/photo-1596422846543-75c6fc197f11?w=2000&q=80",
    tagline: "Truly Asia",
    weather: "25–32°C · tropical year-round",
    bestTime: "Nov – Feb (west coast) · Mar – Oct (east)",
    currency: "MYR (Malaysian Ringgit)",
    inrRate: "1 MYR ≈ ₹19",
    visa: "Free visa for Indians till Dec 2026",
    priceFrom: "From ₹45,000 pp · 5N/6D",
    attractions: [
      { name: "Petronas Twin Towers", img: "https://images.unsplash.com/photo-1596422846543-75c6fc197f11?w=800&q=80", blurb: "KLCC Skybridge + Observation Deck — book early, blue hour is magic." },
      { name: "Langkawi Island Skycab", img: "https://images.unsplash.com/photo-1551632811-561732d1e306?w=800&q=80", blurb: "Rainforest cable car, Sky Bridge, duty-free beach resorts on Cenang." },
      { name: "Genting Highlands", img: "https://images.unsplash.com/photo-1546412414-e1885e51cfa5?w=800&q=80", blurb: "Cool mountain casino resort, 20-Century-Fox theme park, kids-friendly." },
    ],
  },
  europe: {
    name: "Europe",
    hero: "https://images.unsplash.com/photo-1502602898536-47ad22581b52?w=2000&q=80",
    tagline: "Grand Continental Tour",
    weather: "5–28°C · four seasons across countries",
    bestTime: "May – Sep (peak) · Dec (markets)",
    currency: "EUR (Euro) · GBP · CHF",
    inrRate: "1 EUR ≈ ₹92",
    visa: "Schengen visa · 15–20 day process",
    priceFrom: "From ₹1,75,000 pp · 8N/9D · multi-country",
    attractions: [
      { name: "Eiffel Tower, Paris", img: "https://images.unsplash.com/photo-1502602898536-47ad22581b52?w=800&q=80", blurb: "Summit-level ticket, Seine river dinner cruise, and a Louvre skip-the-line." },
      { name: "Interlaken & Jungfrau", img: "https://images.unsplash.com/photo-1527668752968-14dc70a27c95?w=800&q=80", blurb: "Switzerland's adventure capital — paragliding over turquoise lakes." },
      { name: "Rome Colosseum + Vatican", img: "https://images.unsplash.com/photo-1515542622106-78bda8ba0e5b?w=800&q=80", blurb: "Gladiator arena, Sistine Chapel, and a Trevi coin-toss at midnight." },
    ],
  },
  paris: {
    name: "Paris",
    hero: "https://images.unsplash.com/photo-1502602898536-47ad22581b52?w=2000&q=80",
    tagline: "City of Light",
    weather: "3–25°C · cold winters, mild summers",
    bestTime: "Apr – Jun, Sep – Oct",
    currency: "EUR (Euro)",
    inrRate: "1 EUR ≈ ₹92",
    visa: "Schengen visa required · 15-day process",
    priceFrom: "From ₹1,25,000 pp · 4N/5D",
    attractions: [
      { name: "Eiffel Tower Summit", img: "https://images.unsplash.com/photo-1502602898536-47ad22581b52?w=800&q=80", blurb: "Lift to the top + Jules Verne dinner + night sparkle every hour." },
      { name: "Louvre Museum", img: "https://images.unsplash.com/photo-1550340499-a6c60fc8287c?w=800&q=80", blurb: "Mona Lisa, Venus de Milo, and the Napoleon apartments — book early." },
      { name: "Versailles Day Trip", img: "https://images.unsplash.com/photo-1564507592333-c60657eea523?w=800&q=80", blurb: "Hall of Mirrors + Marie Antoinette's hamlet + fountain gardens." },
    ],
  },
  santorini: {
    name: "Santorini",
    hero: "https://images.unsplash.com/photo-1533105079780-92b9be482077?w=2000&q=80",
    tagline: "Cyclades Blue",
    weather: "15–30°C · Aegean, dry summers",
    bestTime: "May – Jun, Sep – Oct (shoulder)",
    currency: "EUR (Euro)",
    inrRate: "1 EUR ≈ ₹92",
    visa: "Schengen visa required · 15-day process",
    priceFrom: "From ₹1,65,000 pp · 5N/6D",
    attractions: [
      { name: "Oia Sunset", img: "https://images.unsplash.com/photo-1533105079780-92b9be482077?w=800&q=80", blurb: "Earth's most photographed sunset — arrive 90 min early for a castle-wall spot." },
      { name: "Caldera Catamaran", img: "https://images.unsplash.com/photo-1601581875039-e899893d520c?w=800&q=80", blurb: "Half-day sail past Red, White & Black beaches + onboard Greek BBQ." },
      { name: "Akrotiri Minoan Ruins", img: "https://images.unsplash.com/photo-1555993539-1732b0258235?w=800&q=80", blurb: "Pompeii of the Aegean — 3,600-year-old Bronze Age city preserved in ash." },
    ],
  },
};

// Aliases map — so "srilanka", "sri-lanka", "ceylon" all resolve to the Sri Lanka entry.
const ALIASES = {
  srilanka: "sri lanka",
  "sri-lanka": "sri lanka",
  ceylon: "sri lanka",
  viet: "vietnam",
  swiss: "switzerland",
  jpn: "japan",
  "japan tour": "japan",
  uae: "dubai",
  "abu dhabi": "dubai",
  phuket: "thailand",
  bangkok: "thailand",
  krabi: "thailand",
  pattaya: "thailand",
  bkk: "thailand",
  kl: "malaysia",
  "kuala lumpur": "malaysia",
  langkawi: "malaysia",
  "ko samui": "thailand",
  mauritius: "maldives", // visually similar fallback until we add full entry
  "hong kong": "singapore", // temporary sibling
  leh: "ladakh",
  kathmandu: "nepal",
  pokhara: "nepal",
  thimphu: "bhutan",
  paro: "bhutan",
  istanbul: "turkey",
  cappadocia: "turkey",
  rome: "europe",
  venice: "europe",
  london: "europe",
  athens: "greece",
  mykonos: "greece",
  zurich: "switzerland",
  interlaken: "switzerland",
  jungfrau: "switzerland",
  ubud: "bali",
  seminyak: "bali",
  "hoi an": "vietnam",
  "ha long": "vietnam",
  hanoi: "vietnam",
  tokyo: "japan",
  kyoto: "japan",
  osaka: "japan",
  "port blair": "andaman",
  havelock: "andaman",
};

const GENERIC_ATTRACTIONS = [
  { name: "Handpicked Stays", img: "https://images.unsplash.com/photo-1566073771259-6a8506099945?w=800&q=80", blurb: "Boutique + 5★ hotels shortlisted by our team over 20 years." },
  { name: "Private Experiences", img: "https://images.unsplash.com/photo-1551632811-561732d1e306?w=800&q=80", blurb: "Local guides, not coach tours — every day designed around you." },
  { name: "Curated Dining", img: "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=800&q=80", blurb: "Reservations at the chefs our travellers recommend the most." },
];

// Reliable generic travel hero (used when no curated match found). Unsplash's
// `source.unsplash.com/featured?keyword` endpoint is unreliable — use a static URL.
const GENERIC_HERO = "https://images.unsplash.com/photo-1476514525535-07fb3b4ae5f1?w=2000&q=80";

// Smart title-case: "sri lanka" -> "Sri Lanka", "srilanka" -> "Srilanka",
// "NEW ZEALAND" -> "New Zealand". Keeps small words lowercase ("the", "of").
function titleCase(input) {
  const SMALL = new Set(["of", "and", "the", "a", "an", "in", "on", "at", "for", "to"]);
  return String(input)
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .map((w, i) =>
      i > 0 && SMALL.has(w) ? w : w.charAt(0).toUpperCase() + w.slice(1)
    )
    .join(" ");
}

export function getSpotlight(destination) {
  if (!destination) return null;
  const raw = String(destination).trim();
  const key = raw.toLowerCase();

  // 1. Direct match
  if (SPOTLIGHTS[key]) return SPOTLIGHTS[key];

  // 2. Alias match (e.g., "srilanka" -> "sri lanka")
  if (ALIASES[key] && SPOTLIGHTS[ALIASES[key]]) return SPOTLIGHTS[ALIASES[key]];

  // 3. Fuzzy substring
  for (const [k, v] of Object.entries(SPOTLIGHTS)) {
    if (key.includes(k) || k.includes(key)) return v;
  }
  for (const [alias, canonical] of Object.entries(ALIASES)) {
    if (key.includes(alias) && SPOTLIGHTS[canonical]) return SPOTLIGHTS[canonical];
  }

  // 4. Smart fallback — title-cased name, reliable hero, credible generic copy.
  //    Used as an INSTANT placeholder while fetchSpotlightRemote() loads the AI-generated
  //    version from the backend.
  const name = titleCase(raw);
  return {
    name,
    hero: GENERIC_HERO,
    tagline: "A Vacations Pro Destination",
    weather: "Pleasant travel weather year-round",
    bestTime: "Peak season: Oct – Mar · shoulder: Apr – Sep",
    currency: "Local currency — live FX on your custom quote",
    inrRate: "Shared with your PDF itinerary",
    visa: "Visa guidance included in your quote",
    priceFrom: "Custom pricing · request free quote",
    attractions: GENERIC_ATTRACTIONS,
    _pending: true, // hint: async fetch can replace this with a richer card
  };
}

// Returns true if this destination has a curated (static) card; false if the instant
// fallback is the best we have locally. DestinationSpotlight uses this to decide whether
// to fire the remote AI fetch.
export function hasCuratedSpotlight(destination) {
  if (!destination) return false;
  const key = String(destination).trim().toLowerCase();
  if (SPOTLIGHTS[key]) return true;
  if (ALIASES[key] && SPOTLIGHTS[ALIASES[key]]) return true;
  for (const k of Object.keys(SPOTLIGHTS)) {
    if (key.includes(k) || k.includes(key)) return true;
  }
  for (const [alias, canonical] of Object.entries(ALIASES)) {
    if (key.includes(alias) && SPOTLIGHTS[canonical]) return true;
  }
  return false;
}

// Remote fetch — calls /api/spotlight/{dest}. Used only when hasCuratedSpotlight() is
// false, so popular destinations stay instant (no network call) and long-tail ones get
// an AI-generated card on demand (then cached server-side forever).
export async function fetchSpotlightRemote(destination) {
  const raw = String(destination || "").trim();
  if (!raw) return null;
  const base = process.env.REACT_APP_BACKEND_URL || "";
  const res = await fetch(`${base}/api/spotlight/${encodeURIComponent(raw)}`, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
  });
  if (!res.ok) throw new Error(`spotlight ${res.status}`);
  return await res.json();
}
